import React, { useState, useEffect, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: inline-block;
    height: 1em;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
    width: 100%;
  }
  .skeleton-circle {
    width: 45px;
    height: 45px;
    border-radius: 10%;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
  }
`;

const TableRow = memo(({ agent, rank, target }) => {
    const count = agent.count;
    const percentage = Math.round((count / target) * 100);
    const progressWidth = Math.min(percentage, 100);

     return (
        <tr>
            <td>
                <div className="d-flex align-items-center">
                    {rank === 1 && <i className="ki-outline ki-crown fs-1 text-warning me-2"></i>}
                    {rank === 2 && <i className="ki-outline ki-medal-star fs-2 text-gray-400 me-2"></i>}
                    {rank === 3 && <i className="ki-outline ki-medal-star fs-2 text-bronze me-2" style={{color: '#cd7f32'}}></i>}
                    <span className="fw-bold text-dark fs-6 ms-1">{rank}</span>
                </div>
            </td>
            <td>
                <div className="d-flex align-items-center">
                    <div className="symbol symbol-45px me-3">
                        <img 
                            src={agent.image} 
                            alt={agent.name} 
                            className="object-fit-cover"
                            loading="lazy" 
                        />
                    </div>
                    <div className="d-flex justify-content-start flex-column">
                        <span className="text-dark fw-bold text-hover-primary fs-6">{agent.name}</span>
                        <span className="text-muted fw-semibold text-muted d-block fs-7">{agent.email}</span>
                    </div>
                </div>
            </td>
            <td className="text-end">
                <span className="badge badge-light-primary fs-6 fw-bold">
                    {count}
                </span>
            </td>
            <td className="text-end">
                <div className="d-flex flex-column w-100 me-2">
                    <div className="d-flex flex-stack mb-1">
                        <span className="text-muted me-2 fs-7 fw-bold">{percentage}%</span>
                    </div>
                    <div className="progress h-6px w-100">
                        <div 
                            className="progress-bar bg-primary" 
                            role="progressbar" 
                            style={{width: `${progressWidth}%`}} 
                        ></div>
                    </div>
                </div>
            </td>
            <td className="text-end">
                <span className="badge badge-light fw-bold">{agent.lga}</span>
            </td>
        </tr>
    );
});

const Ranking = () => {
  const BASE_URL = 'https://adera-web-api.azurewebsites.net/v1';
  const TARGET_GOAL = 2000;

  const [loading, setLoading] = useState(true);
  const [rankingData, setRankingData] = useState([]);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        const agentsPromise = fetch(`${BASE_URL}/admin/agent?page=1&count=2000`, { headers });
        const adminPromise = fetch(`${BASE_URL}/admin`, { headers });

        adminPromise
            .then(res => res.json())
            .then(data => { if (data.ok) setAdmin(data.data); })
            .catch(err => console.log('Admin fetch silent fail'));

        const agentsRes = await agentsPromise;
        const agentsJson = await agentsRes.json();

        if (agentsJson.data?.pageData) {
            const processed = agentsJson.data.pageData.map(agent => ({
                id: agent.id,
                name: `${agent.firstName} ${agent.lastName}`,
                image: agent.avatarUrl || 'assets/media/svg/files/blank-image.svg',
                email: agent.email,
                lga: agent.assignedLga || 'N/A',
                count: agent.client?.total || 0,
                rank: agent.rank || 9999
            }));
            
            processed.sort((a, b) => {
                if (a.rank !== b.rank) return a.rank - b.rank;
                return b.count - a.count;
            });

            setRankingData(processed);
        }

      } catch (error) {
        console.error("Error fetching ranking data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const chartConfig = useMemo(() => {
    const top15 = rankingData.slice(0, 15);
    return {
        data: {
            labels: top15.map(agent => agent.name.split(' ')[0]),
            datasets: [{
                label: 'Total Ads',
                data: top15.map(agent => agent.count),
                backgroundColor: '#009EF7',
                borderRadius: 4,
                barThickness: 30,
            }],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: { 
                    display: true, 
                    text: 'Top 15 Agents (By Rank)',
                    font: { size: 13, weight: 'normal' },
                    color: '#a1a5b7'
                } 
            },
            scales: {
                y: { beginAtZero: true, grid: { borderDash: [2, 2] }, ticks: { stepSize: 20 } },
                x: { grid: { display: false } }
            }
        }
    };
  }, [rankingData]);

  return (
    <>
    <style>{skeletonStyles}</style>
    <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
      <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
        
        <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/">
                  <img alt="Logo" src="assets/aderalogo.png" className="h-60px" />
                </Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                        <div className="menu-item here show menu-here-bg  me-2"><Link to="/dashboarda" className="menu-link"><span className="menu-title">Home</span></Link></div>
                        <div className="menu-item me-2"><Link to="/ad-management" className="menu-link"><span className="menu-title">Advertisement</span></Link></div>
                        <div className="menu-item me-2"><Link to="/agents" className="menu-link"><span className="menu-title">Agents</span></Link></div>
                        <div className="menu-item me-2"><Link to="/payments" className="menu-link"><span className="menu-title">Payments</span></Link></div>
                    </div>
                   </div>
                </div>
                 <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                    <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                        <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                        <span className="text-gray-800 fs-7 fw-bold">{admin ? admin.firstName : 'EDSAA Admin'}</span>
                    </div>
                    <div className="cursor-pointer symbol symbol-circle symbol-40px">
                        <img src={admin?.avatarUrl || "assets/edsal.png"} alt="User" />
                    </div>
                 </div>
              </div>
            </div>
        </div>

        <div className="app-wrapper flex-column flex-column-fluid" id="kt_app_wrapper">
          <div className="app-container container-fluid">
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid pt-10">
                    
                    <div
                      className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-5 p-lg-10 mb-10"
                      style={{ background: 'linear-gradient(90deg, #20AA3E 0%, #03A588 100%)' }}
                    >
                      <div className="my-2 me-5">
                        <div className="fs-2x fw-bold text-white mb-2">
                          Performance Ranking
                        </div>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-4 my-1">
                            <li className="breadcrumb-item text-white ">Home</li>
                            <li className="breadcrumb-item">
                            <span className="bullet bg-white w-4px h-4px"></span>
                            </li>
                            <li className="breadcrumb-item text-white ">Ranking</li>
                        </ul>
                      </div>
                    </div>

                    <div className="card mb-8 shadow-sm">
                        <div className="card-header border-0 pt-5">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bold fs-3 mb-1">Agent Leaderboard</span>
                            </h3>
                        </div>
                        
                        <div className="card-body py-3">
                            <div className="row g-5 g-xl-8">
                                <div className="col-xl-12">
                                    <div className="border rounded p-5 mb-5" style={{ height: '350px' }}>
                                        {loading ? (
                                             <div className="d-flex h-100 w-100 align-items-end justify-content-between px-5">
                                                {[...Array(15)].map((_, i) => (
                                                    <div key={i} className="skeleton-box" style={{ 
                                                        height: `${Math.random() * 60 + 20}%`, 
                                                        width: '30px', 
                                                        borderRadius: '4px',
                                                        marginBottom: '0'
                                                    }}></div>
                                                ))}
                                            </div>
                                        ) : rankingData.length > 0 ? (
                                            <Bar data={chartConfig.data} options={chartConfig.options} />
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                                                <span>No data available</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-xl-12">
                                    <div className="table-responsive">
                                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                            <thead>
                                                <tr className="fw-bold text-muted">
                                                    <th className="min-w-50px">Rank</th>
                                                    <th className="w-300px">Agent</th>
                                                    <th className="min-w-100px text-end">Total Ads</th>
                                                    <th className="min-w-150px">Target</th>
                                                    <th className="min-w-100px text-end">Location</th>
                                                 </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                   Array.from({ length: 8 }).map((_, idx) => (
                                                    <tr key={`skel-${idx}`}>
                                                       <td><div className="skeleton-box" style={{width: '20px'}}></div></td>
                                                       <td>
                                                        <div className="d-flex align-items-center">
                                                           <div className="skeleton-circle me-3"></div>
                                                           <div className="w-100">
                                                              <div className="skeleton-box mb-1" style={{ width: '60%' }}></div>
                                                           </div>
                                                        </div>
                                                       </td>
                                                      <td colSpan="3"><div className="skeleton-box"></div></td>
                                                     </tr>
                                                  ))
                                                ) : (
                                                    <>
                                                        {rankingData.map((agent, index) => (
                                                            <TableRow 
                                                                key={agent.id} 
                                                                agent={agent} 
                                                                rank={agent.rank || index + 1}
                                                                target={TARGET_GOAL}
                                                            />
                                                        ))}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <div className="app-footer py-10 mt-10 border-top">
                    <div className="text-center">
                        <span className="text-muted fw-semibold me-1">2026©</span>
                        <a href="https://edsaa.com.ng/" target="_blank" rel="noopener noreferrer" className="text-gray-800 text-hover-primary fw-bold">EDSAA - ADERA</a>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Ranking;